import { Component, OnInit, inject } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import dayjs from 'dayjs';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { DashboardComponent } from '../dashboard.component';
import {
  Accordion,
  AccordionPanel,
  AccordionHeader,
  AccordionContent,
} from 'primeng/accordion';
import { Ripple } from 'primeng/ripple';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PrimeTemplate } from 'primeng/api';
import { DashboardFormWrapperComponent } from './dashboard-form-wrapper.component';
import { InputGroup } from 'primeng/inputgroup';
import { Button } from 'primeng/button';
import { LaddaModule } from 'angular2-ladda';
import { forkJoin } from 'rxjs';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { DashboardService } from '../../../services/dashboard.service';
import {
  Dashboard,
  DashboardEditorContext,
  DashboardEditorTarget,
  DashboardComponentSize,
  WorkflowResultFieldDefinition,
  createDefaultDashboardConfig,
  normalizeDashboard,
} from '../../../models/dashboard';

_('dashboard.title');

@Component({
  selector: 'app-dashboard',
  templateUrl: './edit-dashboard.component.html',
  styleUrls: ['./edit-dashboard.component.scss'],
  imports: [
    Accordion,
    AccordionPanel,
    Ripple,
    AccordionHeader,
    AccordionContent,
    TranslatePipe,
    Select,
    FormsModule,
    PrimeTemplate,
    DashboardFormWrapperComponent,
    InputGroup,
    Button,
    LaddaModule,
    TranslatePipe,
    ErrorComponent,
    HeaderBarComponent,
  ],
})
export class EditDashboardComponent implements OnInit {
  private readonly betaWidgets = new Set([
    'JobResultAnalyticsDashboardComponent',
    'AveragePickTimeDashboardComponent',
    'DailyScanTimesDashboardComponent',
  ]);

  private dashboardService = inject(DashboardService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);

  date = dayjs();

  isLoading = true;

  error?: HttpErrorResponse;

  dashboard!: Dashboard;

  editorTarget: DashboardEditorTarget = { type: 'organization' };

  editorContext: DashboardEditorContext = {
    target: this.editorTarget,
    resultFields: [],
  };

  editorTitle = '';

  backRoute?: string;

  componentRegistry = DashboardComponent.componentRegistry;

  widgetTypes = Object.keys(this.componentRegistry).map((key) => key);

  newWidget: string | undefined = undefined;

  sizeOptions = Object.entries(DashboardComponentSize).map(([key, value]) => ({
    key,
    value: value as DashboardComponentSize,
  }));

  ngOnInit() {
    const templateId = this.route.snapshot.paramMap.get('id');
    if (templateId) {
      this.initWorkflowTemplateDashboard(templateId);
      return;
    }

    this.editorTitle = this.translate.instant(
      'dashboard.edit.organizationTitle',
    );
    this.loadDashboard();
  }

  private initWorkflowTemplateDashboard(templateId: string) {
    this.editorTarget = { type: 'workflowTemplate', templateId };
    this.backRoute = '/workflow-templates';
    forkJoin({
      dashboard: this.dashboardService.getDashboard(this.editorTarget),
      template: this.dashboardService.getWorkflowTemplate(templateId),
      definition:
        this.dashboardService.getWorkflowTemplateDefinition(templateId),
    }).subscribe({
      next: ({ dashboard, template, definition }) => {
        this.dashboard = normalizeDashboard(dashboard);
        this.editorContext = {
          target: this.editorTarget,
          resultFields: this.getResultFields(definition),
        };
        this.editorTitle = this.translate.instant(
          'dashboard.edit.workflowTemplateTitle',
          { name: template.name },
        );
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = err;
        this.isLoading = false;
      },
    });
  }

  private loadDashboard() {
    this.dashboardService.getDashboard(this.editorTarget).subscribe({
      next: (dashboard) => {
        this.dashboard = normalizeDashboard(
          dashboard,
          createDefaultDashboardConfig(),
        );
        this.editorContext = {
          target: this.editorTarget,
          resultFields: [],
        };
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = err;
        this.isLoading = false;
      },
    });
  }

  saveDashboard() {
    if (this.dashboard) {
      this.error = undefined;
      this.isLoading = true;
      this.dashboardService
        .saveDashboard(this.dashboard, this.editorTarget)
        .subscribe({
          next: () => {
            this.isLoading = false;
          },
          error: (err: HttpErrorResponse) => {
            this.error = err;
            this.isLoading = false;
          },
        });
    }
  }

  addWidget() {
    const widgetName = this.translate.instant(
      `dashboard.edit.${this.newWidget}.name`,
    );
    const newWidget = {
      component: this.newWidget!,
      size: DashboardComponentSize.HALF,
      configuration: {
        title: widgetName,
        description: '',
      },
    };
    this.dashboard.configuration.items.push(newWidget);
  }

  moveWidgetUp(index: number) {
    if (this.dashboard && index > 0) {
      const temp = this.dashboard.configuration.items[index - 1];
      this.dashboard.configuration.items[index - 1] =
        this.dashboard.configuration.items[index];
      this.dashboard.configuration.items[index] = temp;
    }
  }

  moveWidgetDown(index: number) {
    if (
      this.dashboard &&
      index < this.dashboard.configuration.items.length - 1
    ) {
      const temp = this.dashboard.configuration.items[index + 1];
      this.dashboard.configuration.items[index + 1] =
        this.dashboard.configuration.items[index];
      this.dashboard.configuration.items[index] = temp;
    }
  }

  removeWidget(index: number) {
    if (
      this.dashboard &&
      index >= 0 &&
      index < this.dashboard.configuration.items.length
    ) {
      this.dashboard.configuration.items.splice(index, 1);
    }
  }

  isBetaWidget(widgetType: string | undefined): boolean {
    return !!widgetType && this.betaWidgets.has(widgetType);
  }

  private getResultFields(
    definition: Record<string, unknown>,
  ): WorkflowResultFieldDefinition[] {
    const fields = definition?.['resultFields'];
    if (!Array.isArray(fields)) {
      return [];
    }
    return fields.filter(
      (field): field is WorkflowResultFieldDefinition =>
        typeof field === 'object' &&
        field !== null &&
        typeof (field as WorkflowResultFieldDefinition).key === 'string' &&
        ['STRING', 'NUMBER', 'BOOLEAN', 'TIMESTAMP'].includes(
          (field as WorkflowResultFieldDefinition).type,
        ),
    );
  }
}
